import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";

import { baseQueryWithReauth } from "../setup";

// Define a service using a base URL and expected endpoints
export const organizationApi = createApi({
  reducerPath: "organizationApi",
  baseQuery: baseQueryWithReauth,
  tagTypes: ["Organizations", "NotificationOptions", "Organization", "DeletedOrganizations"],
  endpoints: (builder) => ({
    getOrganizationList: builder.query({
      query: () => {
        return { url: `/organization/list`, method: "GET" };
      },
      providesTags: (result) => {
        return result
          ? [
              ...result.map(({ id }) => ({ type: "Organizations", id })),
              { type: "Organizations", id: "LIST" },
            ]
          : [{ type: "Organizations", id: "LIST" }];
      },
    }),
    createOrganization: builder.mutation({
      query: (payload) => {
        return {
          url: `/organization/create`,
          method: "POST",
          body: { ...payload },
        };
      },
      invalidatesTags: [{ type: "Organizations", id: "LIST" }],
    }),
    getOrganizationData: builder.query({
      query: (id) => {
        return { url: `/organization/export/${id}`, method: "GET" };
      },
      providesTags: (result, error, id) => [{ type: 'Organization', id }],
    }),
    importOrganization : builder.mutation({
      query: (payload) => {
        return {
          url: `/organization/import`,
          method: "POST",
          body: { ...payload },
        };
      },
      invalidatesTags: [{ type: "Organizations", id: "LIST" }],
    }),
    copyOrganization: builder.mutation({
      query: (payload) => {
        return {
          url: `/organization/copy`,
          method: "POST",
          body: { ...payload },
        };
      },
      invalidatesTags: [{ type: "Organizations", id: "LIST" }],
    }),
    updateOrganization: builder.mutation({
      query: ({ id, payload }) => {
        return {
          url: `/organization/${id}`,
          method: "PATCH",
          body: { ...payload },
        };
      },
      invalidatesTags: [{ type: "Organizations", id: "LIST" }],
    }),
    deleteOrganization: builder.mutation({
      query: (id) => {
        return {
          url: `/organization/${id}`,
          method: "DELETE",
        };
      },
      invalidatesTags: [{ type: "Organizations", id: "LIST" }],
    }),
    getOrganizationNotificationOptionsCustomer: builder.query({
      query: (subdomain) => {
        return { url: `/organization/${subdomain}/customer/notifications/list`, method: "GET" };
      },
      providesTags: (result) => {
        return result
          ? [
            ...result.map(({ id }) => ({ type: "NotificationOptions", id })),
            { type: "NotificationOptions", id: "LIST" },
          ]
          : [{ type: "NotificationOptions", id: "LIST" }];
      },
    }),
    getOrganizationNotificationOptions: builder.query({
      query: (id) => {
        return { url: `/organization/${id}/notifications/list`, method: "GET" };
      },
      providesTags: (result) => {
        return result
          ? [
              ...result.map(({ id }) => ({ type: "NotificationOptions", id })),
              { type: "NotificationOptions", id: "LIST" },
            ]
          : [{ type: "NotificationOptions", id: "LIST" }];
      },
    }),
    updateOrganizationNotificationOptions: builder.mutation({
      query: ({ id, notificationId, active }) => {
        return {
          url: `/organization/${id}/notifications/${notificationId}`,
          method: "PATCH",
          body: { active },
        };
      },
      invalidatesTags: (result, error, { notificationId }) => [
        { type: "NotificationOptions", notificationId },
      ],
    }),
	  getDeletedOrganizationList: builder.query({
		  query: () => {
			  return { url: `/organization/deleted`, method: "GET" };
		  },
		  providesTags: (result) => {
			  return result
				  ? [
					  ...result.map(({ id }) => ({ type: "DeletedOrganizations", id })),
					  { type: "DeletedOrganizations", id: "LIST" },
				  ]
				  : [{ type: "DeletedOrganizations", id: "LIST" }];
		  },
	  }),
	  recoverOrganization: builder.mutation({
		  query: (id) => {
			  return {
				  url: `/organization/recover/${id}`,
				  method: "PATCH",
			  }
		  },
		  invalidatesTags: [{ type: "Organizations", id: "LIST" }],
	  }),
	  permanentlyDeleteOrganization: builder.mutation({
		  query: (id ) => {
			  return {
				  url: `/organization/permanently/${id}`,
				  method: "DELETE",
			  }
		  }
	  }),
  }),
});

export const {
  useGetOrganizationListQuery,
  useLazyGetOrganizationDataQuery,
  useCreateOrganizationMutation,
  useImportOrganizationMutation,
  useCopyOrganizationMutation,
  useUpdateOrganizationMutation,
  useDeleteOrganizationMutation,
  useLazyGetOrganizationNotificationOptionsQuery,
  useGetOrganizationNotificationOptionsCustomerQuery,
  useUpdateOrganizationNotificationOptionsMutation,
	useLazyGetDeletedOrganizationListQuery,
	useRecoverOrganizationMutation,
	usePermanentlyDeleteOrganizationMutation,
} = organizationApi;
